/**
 * 按设备记 DOSIA 版本（2026-10-16，维护者拍板「侧栏强提醒」配套）的线上契约单源。
 *
 * 生产方：桌面企业版在所有鉴权请求的汇聚点（apps/desktop managed-workset-enterprise-provider
 * `fetchEnvelope`）带上两个头；企业后端鉴权后按 JWT 用户 + `x-dosia-device-id` 记进
 * `enterprise_client_devices`（apps/web/server/enterprise/client-device-store.ts）。
 * 消费方：企业后端服务面 `GET /api/enterprise/service/client-devices` → admin-portal 透传
 * `/api/organizations/:orgCode/client-devices` → 管理后台员工列表「DOSIA 版本」列。
 *
 * 头值来自客户端、不可信：服务端只认下面三个 guard 放行的形状，其余一律当作没带（不记）。
 */
import { parseVersionLagReport } from "./enterprise-version-lag.js";
export const DOSIA_CLIENT_APP_VERSION_HEADER = "x-dosia-app-version";
export const DOSIA_CLIENT_PLATFORM_HEADER = "x-dosia-platform";
export const DOSIA_CLIENT_DEVICE_ID_HEADER = "x-dosia-device-id";
const APP_VERSION_RE = /^\d{1,4}\.\d{1,4}\.\d{1,6}$/;
const PLATFORM_RE = /^[a-z0-9]{2,16}-[a-z0-9_]{2,16}$/;
/** 桌面端是 UUIDv4（device-id.ts）；放宽到不透明 id 字符集，长度 8–64。
 *  审计哨兵 `unknown` / 服务面默认值 `service` 都短于 8，天然被拒。 */
const DEVICE_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;
function headerString(raw) {
    return typeof raw === "string" ? raw.trim() : null;
}
/** `x-dosia-app-version` → `x.y.z`，否则 null。 */
export function parseClientAppVersion(raw) {
    const v = headerString(raw);
    return v && APP_VERSION_RE.test(v) ? v : null;
}
/** `x-dosia-platform` → `<os>-<arch>`（如 `darwin-arm64`），否则 null。 */
export function parseClientPlatform(raw) {
    const v = headerString(raw);
    return v && PLATFORM_RE.test(v) ? v : null;
}
/** `x-dosia-device-id` → 不透明设备 id，否则 null。 */
export function parseClientDeviceId(raw) {
    const v = headerString(raw);
    return v && DEVICE_ID_RE.test(v) ? v : null;
}
function isRecord(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
function parseDevice(raw) {
    if (!isRecord(raw))
        return null;
    const { userId, deviceId, appVersion, platform, firstSeenAt, lastSeenAt } = raw;
    if (typeof userId !== "string" || !userId
        || typeof deviceId !== "string" || !deviceId
        || typeof appVersion !== "string" || !appVersion
        || typeof platform !== "string"
        || typeof firstSeenAt !== "string"
        || typeof lastSeenAt !== "string") {
        return null;
    }
    return { userId, deviceId, appVersion, platform, firstSeenAt, lastSeenAt };
}
/**
 * 反序列化 guard（管理后台读透传结果用）。坏行丢弃；整体不是契约形状就抛——
 * 「契约漂移伪装成没有数据」是本仓反复出现的故障形态，宁可报错也不回空列表。
 */
export function parseEnterpriseClientDevicesResponse(raw) {
    if (!isRecord(raw) || !Array.isArray(raw.devices)) {
        throw new Error("client-devices response is not { devices: [...] }");
    }
    const devices = raw.devices
        .map(parseDevice)
        .filter((d) => d !== null);
    const floor = raw.minRecommendedVersion;
    return {
        devices,
        minRecommendedVersion: typeof floor === "string" && APP_VERSION_RE.test(floor) ? floor : null,
        // 名单是附加信息：缺失或坏都退回「只按设备显示」，不让整列变「—」。
        versionLag: parseVersionLagReport(raw.versionLag),
    };
}
//# sourceMappingURL=enterprise-client-devices.js.map